import { ArrowDownRight, ArrowUpRight, Bookmark, Download } from "lucide-react";
import { useCity } from "../state/CityContext";
import { SCENARIOS } from "../data/city";
import { STEP } from "../simulation/types";
import { allocatedTotal } from "../simulation/engine";
import { downloadRun } from "../lib/persistence";
import { clock, Dialog, number } from "./Primitives";
export function OutcomeDialog({ onClose }: { onClose: () => void }) {
  const { state, dispatch, current, history, baseline, comparison } = useCity();
  const index = state.run.minute / STEP,
    noResponse = baseline[index],
    pinned = comparison?.history[index];
  const end = history.at(-1)!,
    endBase = baseline.at(-1)!,
    endPin = comparison?.history.at(-1);
  const delta = current.continuity - noResponse.continuity,
    savedHours = endBase.disruptionHours - end.disruptionHours;
  return (
    <Dialog title="Your decisions, measured." onClose={onClose} wide>
      <p className="dialog-intro">
        {SCENARIOS[state.run.scenario].label} · {clock(state.run.minute)} ·{" "}
        {allocatedTotal(current.allocations)} of 8 crews deployed
      </p>
      <div className="outcome-hero">
        <div>
          <span className="eyebrow">CITY CONTINUITY / NOW</span>
          <strong>
            {Math.round(current.continuity)}
            <small>%</small>
          </strong>
        </div>
        <div>
          <span className="outcome-delta">
            <ArrowUpRight size={20} /> +{delta.toFixed(1)}
          </span>
          <p>
            percentage points against
            <br />
            the same event with no response
          </p>
        </div>
      </div>
      <table className="outcome-table">
        <caption>Comparison at {clock(state.run.minute)}</caption>
        <thead>
          <tr>
            <th>Measure</th>
            <th>No response</th>
            <th>Your plan</th>
            {pinned && <th>Pinned plan</th>}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>Continuity</th>
            <td>{noResponse.continuity.toFixed(1)}%</td>
            <td>{current.continuity.toFixed(1)}%</td>
            {pinned && <td>{pinned.continuity.toFixed(1)}%</td>}
          </tr>
          <tr>
            <th>Mobility</th>
            <td>{noResponse.mobility.toFixed(1)}%</td>
            <td>{current.mobility.toFixed(1)}%</td>
            {pinned && <td>{pinned.mobility.toFixed(1)}%</td>}
          </tr>
          <tr>
            <th>Energy</th>
            <td>{noResponse.energy.toFixed(1)}%</td>
            <td>{current.energy.toFixed(1)}%</td>
            {pinned && <td>{pinned.energy.toFixed(1)}%</td>}
          </tr>
          <tr>
            <th>Households affected</th>
            <td>{number(noResponse.affected)}</td>
            <td>{number(current.affected)}</td>
            {pinned && <td>{number(pinned.affected)}</td>}
          </tr>
        </tbody>
      </table>
      <div className="outcome-projection">
        <div>
          <span className="eyebrow">FULL 120-MINUTE EXPERIMENT</span>
          <p>
            Uses all recorded decisions, including those after the current
            playback time.
          </p>
        </div>
        <strong>
          <ArrowDownRight size={22} />
          {number(savedHours)}
          <small>household-hours of disruption avoided</small>
        </strong>
        <p>
          {number(end.disruptionHours)} household-hours with your plan, against{" "}
          {number(endBase.disruptionHours)} without response.
          {endPin ? ` Pinned plan: ${number(endPin.disruptionHours)}.` : ""}
        </p>
      </div>
      {state.comparison && (
        <p className="comparison-note">
          Pinned scenario: {SCENARIOS[state.comparison.scenario].label}.
          Comparisons use the same playback time. Different parameters compare
          different experiments, rather than isolating a response effect.
        </p>
      )}
      <div className="dialog-actions">
        <button
          className="primary-button"
          onClick={() => dispatch({ type: "compare" })}
        >
          <Bookmark size={16} />
          {state.comparison ? "Replace pinned plan" : "Pin this plan"}
        </button>
        <button
          className="secondary-button"
          onClick={() => downloadRun(state.run, state.comparison)}
        >
          <Download size={16} />
          Export experiment
        </button>
      </div>
      <p className="small-note">
        All measures and household counts are synthetic. The continuity score is
        a weighted stress index, not a measured availability or real-world
        forecast.
      </p>
    </Dialog>
  );
}
